/**
 * The three public FLWR Club membership tiers.
 * Single source of truth for the landing page, membership page and settings.
 */

export type MembershipKey = "free" | "vip" | "club" | "pro";

export type MembershipTier = {
  key: MembershipKey;
  label: string;
  priceLabel: string;
  /** Stripe price lookup key: null for the free tier. */
  priceId: string | null;
  tagline: string;
  badge?: string;
  bullets: string[];
  cta: string;
};

export const MEMBERSHIP_TIERS: MembershipTier[] = [
  {
    key: "free",
    label: "Free Member",
    priceLabel: "$0",
    priceId: null,
    tagline: "Never miss a date. Order gifts on your own terms.",
    bullets: [
      "Free sign-up: no card required",
      "Manual gift ordering any time",
      "Standard event reminders for every saved date",
      "Discreet profile isolation on every recipient",
    ],
    cta: "Sign Up for Free",
  },
  {
    key: "vip",
    label: "VIP Autopilot",
    priceLabel: "$9.99",
    priceId: "vip_autopilot_monthly",
    badge: "Most popular",
    tagline: "Every saved date handled without you lifting a finger.",
    bullets: [
      "Auto-ship on every saved date",
      "Gifts billed separately at the budget you set per date",
      "15% off all products",
      "$0 curation fees",
      "Duplicate card note shield",
      "Discreet lock-screen alerts",
    ],

    cta: "Go VIP Autopilot",
  },
  {
    key: "club",
    label: "Monthly Floral Club",
    priceLabel: "$129",
    priceId: "monthly_floral_club_monthly",
    tagline: "Fresh luxury florals delivered every single month.",
    bullets: [
      "One Signature floral drop every month, included in your $129 with no extra charge",
      "Hand-arranged by vetted local florists",
      "All VIP Autopilot perks included",
      "FLWR Club Pro client tools included at no extra charge",
      "White-glove concierge line",
    ],

    cta: "Join the Floral Club",
  },
];

/**
 * FLWR Club Pro is the business plan. It is intentionally NOT part of
 * MEMBERSHIP_TIERS: the consumer pricing grid stays three cards, and Pro is
 * sold on its own /business page.
 */
export const PRO_TIER: MembershipTier = {
  key: "pro",
  label: "FLWR Club Pro",
  priceLabel: "$49",
  priceId: "pro_monthly",
  badge: "For business",
  tagline: "Turn one-time clients into lifelong referral engines.",
  bullets: [
    "Client address book with bulk autopilot and reminder controls",
    "Closing anniversary engine: years 1 through 5 set once",
    "Bulk CSV client import straight from your CRM",
    "Branded card signature on every gift card",
    "Address confirmation text before every drop",
    "Expense-ready statements you can hand to a bookkeeper",
    "15% off all products and $0 curation fees",
  ],
  cta: "Start FLWR Club Pro",
};

export function getMembershipTier(key: string): MembershipTier | undefined {
  if (key === "pro") return PRO_TIER;
  return MEMBERSHIP_TIERS.find((t) => t.key === key);
}
